import { lazy } from "react";
import { createBrowserRouter } from "react-router";
import { ProtectedRoute } from "@/components/protected-route";
import { AdminGuard } from "@/components/admin-guard";
import { DashboardLayout } from "@/components/dashboard-layout";
import { RouteError, NotFoundPage } from "@/components/route-error";
import { LandingPage } from "@/pages/landing";
import { LoginPage } from "@/pages/auth/login";
import { SignupPage } from "@/pages/auth/signup";
import { ForgotPasswordPage } from "@/pages/auth/forgot-password";
import { ResetPasswordPage } from "@/pages/auth/reset-password";
import { VerifyEmailSentPage } from "@/pages/auth/verify-email";
import { TermsPage } from "@/pages/legal/terms";
import { PrivacyPage } from "@/pages/legal/privacy";

// Authenticated surfaces are code-split so the marketing + auth entry stays light.
const DashboardPage = lazy(() =>
  import("@/pages/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const CreateWorkerPage = lazy(() =>
  import("@/pages/create-worker").then((m) => ({ default: m.CreateWorkerPage })),
);
const WorkerDetailPage = lazy(() =>
  import("@/pages/worker-detail").then((m) => ({ default: m.WorkerDetailPage })),
);
const SettingsPage = lazy(() =>
  import("@/pages/settings").then((m) => ({ default: m.SettingsPage })),
);
const TopupPage = lazy(() => import("@/pages/topup").then((m) => ({ default: m.TopupPage })));
const AdminIndexPage = lazy(() =>
  import("@/pages/admin").then((m) => ({ default: m.AdminIndexPage })),
);
const AdminMcpServersPage = lazy(() =>
  import("@/pages/admin/mcp-servers").then((m) => ({ default: m.AdminMcpServersPage })),
);
const AdminInvitesPage = lazy(() =>
  import("@/pages/admin/invites").then((m) => ({ default: m.AdminInvitesPage })),
);
const AdminUsersPage = lazy(() =>
  import("@/pages/admin/users").then((m) => ({ default: m.AdminUsersPage })),
);

export const router = createBrowserRouter([
  { path: "/", element: <LandingPage />, errorElement: <RouteError /> },
  { path: "/login", element: <LoginPage />, errorElement: <RouteError /> },
  { path: "/signup", element: <SignupPage />, errorElement: <RouteError /> },
  { path: "/forgot-password", element: <ForgotPasswordPage />, errorElement: <RouteError /> },
  { path: "/reset-password", element: <ResetPasswordPage />, errorElement: <RouteError /> },
  { path: "/verify-email-sent", element: <VerifyEmailSentPage />, errorElement: <RouteError /> },
  { path: "/terms", element: <TermsPage />, errorElement: <RouteError /> },
  { path: "/privacy", element: <PrivacyPage />, errorElement: <RouteError /> },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteError />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          { path: "/dashboard", element: <DashboardPage /> },
          { path: "/dashboard/create", element: <CreateWorkerPage /> },
          { path: "/dashboard/workers/:id", element: <WorkerDetailPage /> },
          { path: "/dashboard/settings", element: <SettingsPage /> },
          { path: "/dashboard/topup", element: <TopupPage /> },
          {
            element: <AdminGuard />,
            children: [
              { path: "/admin", element: <AdminIndexPage /> },
              { path: "/admin/mcp-servers", element: <AdminMcpServersPage /> },
              { path: "/admin/invites", element: <AdminInvitesPage /> },
              { path: "/admin/users", element: <AdminUsersPage /> },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
